import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ApiService } from 'src/app/services/api.service';
import { SkuService } from '../../sku.service';
import { DialogService } from 'src/app/core/services/dialog.service';
import { RequestsService } from 'src/app/core/services/http/requests.service';

import { CreateBuyLoadItemRequest } from 'src/app/core/model/server/vo/CreateBuyLoadItemRequest.interface';
import { CreateBuyLoadItemResponse } from 'src/app/core/model/server/vo/CreateBuyLoadItemResponse.interface';
import { Urls } from 'src/app/lib/urls';
import {
  LOAD_PROVIDER_OPTIONS,
  NETWORK_PROVIDER_OPTIONS,
  ITEM_TYPE_OPTIONS,
  NETWORK_PROVIDER_PINS_OPTIONS,
} from 'src/app/lib/constants/dropdown-options.constants';

export interface CreateEditSkuDialogData {
  mode: 'create' | 'edit';
  item?: any; // existing buyload item for edit mode
}

@Component({
  selector: 'app-create-edit-sku-modal',
  templateUrl: './create-edit-sku-modal.component.html',
  styleUrl: './create-edit-sku-modal.component.scss',
})
export class CreateEditSkuModalComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private fetchedItem: any = null;
  form!: UntypedFormGroup;
  isEditMode = false;
  
  // ─── Dropdown Options ──────────────────────────────────────────────────────
  loadProviderOptions         = LOAD_PROVIDER_OPTIONS;
  networkProviderOptions      = NETWORK_PROVIDER_OPTIONS;
  networkProviderPinsOptions  = NETWORK_PROVIDER_PINS_OPTIONS
  itemTypeOptions             = ITEM_TYPE_OPTIONS;
  categoryOptions: any[]      = [];
  
  // ─── Dropdown State ────────────────────────────────────────────────────────
  provider: any;              provider_text: any;
  providerType: any = null;   providerType_text: any;
  itemType: any;              itemType_text: any;
  itemCategory: any;          itemCategory_text: any;
  isGamingPins = false;

  // ─── Entertainment-only provider lock ───────────────────────────────────────
  isItemTypeLocked = false;
  private readonly ENTERTAINMENT_ONLY_PROVIDER_TYPES = ['10', '11', '12'];

  // ─── Redeem (edit only) ────────────────────────────────────────────────────
  isRedeemable = false;

  // ─── Category inline dropdown ──────────────────────────────────────────────
  categoryOpen = false;
  originalIsGamingPins = false;

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<CreateEditSkuModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateEditSkuDialogData,
    private apiService: ApiService,
    private skuService: SkuService,
    private dialogService: DialogService,
    private request: RequestsService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.isEditMode = this.data?.mode === 'edit';

    this.form = this.fb.group({
      itemName:         ['', Validators.required],
      itemId:           ['', Validators.required],
      itemDesc:         ['', Validators.required],
      itemPrice:        ['', [Validators.required, Validators.min(0.01)]],
      rebatePercentage: [null, [Validators.max(100), Validators.min(0)]],
      sort:             [null],
      pointsAmount:     [null],
    });

    if (this.isEditMode && this.data.item?.uid) {
      this.fetchItemThenPatch(this.data.item.uid).catch((err) => { 
        this.toastr.error(err.error?.message || 'Something went wrong!', 'Error!');
      });
    } else {
      this.getActiveCategories();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  get sectionLabel(): string {
    return this.isEditMode ? 'ITEM DETAILS' : 'BUY LOAD SKU DETAILS';
  }

  get title(): string {
    return this.isEditMode ? 'EDIT DETAILS' : 'ADD BUY LOAD SKU';
  }

  private getLabelFromOptions(options: { label: string; value: any }[], value: any): string {
    return options.find(o => o.value === value)?.label ?? '';
  }

  private patchEditValues(item: any): void {
    this.form.patchValue({
      itemName:         item.itemName,
      itemId:           item.itemId,
      itemDesc:         item.itemDesc,
      itemPrice:        item.itemPrice / 100,
      rebatePercentage: item.rebatePercentage ?? 0,
      sort:             item.sort ?? null,
      pointsAmount:     item.pointsAmount ?? null,
    });

    this.form.get('itemId')?.disable();

    this.isGamingPins = item.itemType === 8;
    this.originalIsGamingPins = this.isGamingPins; 

    // providerType — array for gaming pins, scalar for others (mirrors old edit)
    if (this.isGamingPins) {
      if (Array.isArray(item.providerType)) {
        this.providerType = item.providerType.map(String);
      } else if (item.providerType != null) {
        this.providerType = [String(item.providerType)];
      } else {
        this.providerType = [];
      }
    } else {
      this.providerType = item.providerType;
    }

    // Load Provider
    this.provider      = item.provider;
    this.provider_text = this.getLabelFromOptions(this.loadProviderOptions, item.provider)
                      || this.getLabelFromOptions(this.loadProviderOptions, String(item.provider));

    // Network Provider text (mirrors setupDisplayTexts from old edit)
    if (this.isGamingPins && Array.isArray(this.providerType) && this.providerType.length > 0) {
      if (this.providerType.length === 1) {
        this.providerType_text = this.getLabelFromOptions(this.networkProviderOptions, this.providerType[0]);
      } else {
        this.providerType_text = this.providerType
          .map(val => this.getLabelFromOptions(this.networkProviderOptions, val))
          .filter(Boolean)
          .join(' & ');
      }
    } else {
      this.providerType_text = this.getLabelFromOptions(this.networkProviderOptions, item.providerType)
                            || this.getLabelFromOptions(this.networkProviderOptions, String(item.providerType));
      // lock item type if the existing provider type is entertainment-only
      this.isItemTypeLocked = this.ENTERTAINMENT_ONLY_PROVIDER_TYPES.includes(String(item.providerType));
    }

    // Item Type
    this.itemType      = item.itemType;
    this.itemType_text = this.getLabelFromOptions(this.itemTypeOptions, item.itemType)
                      || this.getLabelFromOptions(this.itemTypeOptions, String(item.itemType));

    // Category
    this.itemCategory = item.itemCategory;
    const categoryMatch = this.categoryOptions.find(
      o => o.value === item.itemCategory || o.value === String(item.itemCategory)
    );
    if (item.itemCategory && !categoryMatch) {
      this.categoryOptions = [
        { label: item.itemCategory, value: item.itemCategory },
        ...this.categoryOptions,
      ];
    }
    this.itemCategory_text = categoryMatch?.label ?? item.itemCategory;

    this.isRedeemable = item.redeemable ?? false;
  }

  // ─── Categories ────────────────────────────────────────────────────────────

  getActiveCategories(): void {
    this.apiService.getActiveCategories(1, 100, { isEnabled: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 200 && response.result?.data) {
            this.categoryOptions = response.result.data
              .filter((c: any) => !c.deleteDate)
              .map((c: any) => ({
                label: c.categoryDescription || c.itemCategory,
                value: c.itemCategory,
              }));
          }
        },
        error: () => this.toastr.error('Failed to load categories', 'Error!'),
      });
  }


  private getActiveCategoriesThenPatch(item: any): Promise<void> {
    return new Promise((resolve) => {
      this.apiService.getActiveCategories(1, 100, { isEnabled: true })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.status === 200 && response.result?.data) {
              this.categoryOptions = response.result.data
                .filter((c: any) => !c.deleteDate)
                .map((c: any) => ({
                  label: c.categoryDescription || c.itemCategory,
                  value: c.itemCategory,
                }));
            }
            this.patchEditValues(item);
            resolve();
          },
          error: () => {
            this.toastr.error('Failed to load categories', 'Error!');
            this.patchEditValues(item);
            resolve();
          },
        });
    });
  }

  // Edit mode: load categories first, then patch everything after
  private async fetchItemThenPatch(uid: any): Promise<void> {
    this.spinner.show().catch(console.error);
    try {
      const response = await firstValueFrom(
        this.apiService.getBuyLoadItemById(uid, {})
      );
      const item = response.result;
      this.fetchedItem = item;
      await this.getActiveCategoriesThenPatch(item);
    } catch (error: any) {
      this.toastr.error(error.error?.message || 'Failed to load item details.', 'Error!');
    } finally {
      this.spinner.hide().catch(console.error);
    }
  }


  openCategoryDialog(): void {
    this.dialogService.openAddCategoryDialog()
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getActiveCategories());
  }

  // ─── Dropdown Setters ──────────────────────────────────────────────────────

  setProvider(text: string, value: any): void {
    this.provider      = value;
    this.provider_text = text;
  }

  setProviderType(text: string, value: any): void {
    this.providerType      = value;
    this.providerType_text = text;

    this.isItemTypeLocked = this.ENTERTAINMENT_ONLY_PROVIDER_TYPES.includes(String(value));

    if (this.isItemTypeLocked) {
      this.itemType      = '9';
      this.itemType_text = this.getLabelFromOptions(this.itemTypeOptions, '9');
    }
  }

  setNetworkProvider(selection: any): void {
    if (selection.isDefault) {
      this.providerType      = [];
      this.providerType_text = selection.displayText;
    } else if (selection.selectedValues?.length > 0) {
      this.providerType      = selection.selectedValues;      // keep as strings
      this.providerType_text = selection.displayText;
    } else {
      this.providerType      = [];
      this.providerType_text = '';
    }
  }

  setItemType(text: string, value: any): void {
    const basicResult = this.skuService.setValue(text, value);
    this.itemType      = basicResult.value;
    this.itemType_text = basicResult.text;

    const gamingLogic = this.skuService.checkGamingPinsLogic(text, value, this.isGamingPins);
    this.isGamingPins = gamingLogic.isGamingPins;

    if (gamingLogic.autoSetProviderType) {
      this.providerType      = gamingLogic.autoSetProviderType.value;
      this.providerType_text = gamingLogic.autoSetProviderType.text;
    }

    if (gamingLogic.shouldResetProviderType) {
      this.providerType      = null;
      this.providerType_text = null;
      this.itemCategory      = null;
      this.itemCategory_text = null;
      this.categoryOpen      = false;
    }
  }

  setRedeemable(value: boolean): void {
    this.isRedeemable = value;
  }

  setCategory(text: string, value: any): void {
    this.itemCategory      = value;
    this.itemCategory_text = text;
  }

  toggleCategoryDropdown(): void {
    this.categoryOpen = !this.categoryOpen;
  }

  selectCategory(opt: { label: string; value: any }): void {
    this.itemCategory      = opt.value;
    this.itemCategory_text = opt.label;
    this.categoryOpen      = false;
  }

  closeCategoryDropdown(): void {
    this.categoryOpen = false;
  }

  // ─── Validation ────────────────────────────────────────────────────────────

  private isFormValid(): boolean {
    const c = this.form.controls;
    const itemPrice    = c['itemPrice'].value;
    const rebate       = c['rebatePercentage'].value;

    if (!c['itemName'].value?.trim())                                    { this.toastr.error('Please enter Item Name',                    'Required Field Missing'); return false; }
    if (!this.isEditMode && !this.form.get('itemId')?.value?.trim())     { this.toastr.error('Please enter Item ID',                     'Required Field Missing'); return false; }
    if (!c['itemDesc'].value?.trim())                                    { this.toastr.error('Please enter Item Description',             'Required Field Missing'); return false; }
    if (!itemPrice || itemPrice <= 0)                                    { this.toastr.error('Please enter a valid Item Price',           'Required Field Missing'); return false; }
    if (rebate != null && (rebate < 0 || rebate > 100))                 { this.toastr.error('Rebate Percentage must be between 0 - 100', 'Invalid Value');          return false; }
    if (this.provider == null)                                           { this.toastr.error('Please select Load Provider',              'Required Field Missing'); return false; }
    if (this.itemType == null)                                           { this.toastr.error('Please select Item Type',                  'Required Field Missing'); return false; }
    if (this.isGamingPins && !this.itemCategory)                        { this.toastr.error('Please select Category for Gaming Pins',   'Required Field Missing'); return false; }
    if (!this.isGamingPins && this.providerType == null)                 { this.toastr.error('Please select Network Provider',           'Required Field Missing'); return false; }

    return true;
  }

  // ─── Duplicate check (create only) ────────────────────────────────────────

  private async isExisted(itemId: string, provider: number): Promise<boolean> {
    try {
      const response = await this.request.post(Urls.buyload + '?page=1&size=100', { search: itemId, sortBy: {} });
      if (response.status === 200 && response.result.data) {
        return response.result.data
          .filter((item: any) => item.itemId === itemId)
          .some((item: any) => Number(item.provider) === Number(provider));
      }
      return false;
    } catch {
      return false;
    }
  }

  // ─── Submit ────────────────────────────────────────────────────────────────

  async submit(): Promise<void> {
    if (!this.isFormValid()) { return; }

    if (!this.isEditMode && this.isGamingPins) {
      const itemId = this.form.controls['itemId'].value;
      const duplicateExists = await this.isExisted(itemId, this.provider);
      if (duplicateExists) {
        this.toastr.error('This item ID with the same provider already exists.', 'Duplicate Item');
        return;
      }
    }

    this.spinner.show().catch(console.error);

    const raw = this.form.getRawValue();
    const modifiedItemPrice = raw.itemPrice * 100;

    if (this.isEditMode) {
      await this.performUpdate(raw, modifiedItemPrice);
    } else {
      await this.performCreate(raw, modifiedItemPrice);
    }
  }

  private async performCreate(raw: any, modifiedItemPrice: number): Promise<void> {
    const requestBody: CreateBuyLoadItemRequest = {
      provider:     this.provider,
      itemId:       raw.itemId,
      itemName:     raw.itemName,
      itemDesc:     raw.itemDesc,
      itemPrice:    modifiedItemPrice,
      itemType:     this.itemType,
      itemCategory: this.isGamingPins ? this.itemCategory : null,
      rebatePercentage: raw.rebatePercentage !== 0 ? raw.rebatePercentage : null,
      sort:             raw.sort !== 0 ? raw.sort : null,
      coinsType:    1,
      redeemable:   false,
    };

    if (this.isGamingPins) {
      requestBody.providerTypes = Array.isArray(this.providerType) 
      ? this.providerType.map((v: any) => Number(v)) 
      : [];
      delete requestBody.itemType;
    } else {
      requestBody.providerType = this.providerType;
      delete requestBody.providerTypes;
      delete requestBody.coinsType;
    }

    try {
      const response: CreateBuyLoadItemResponse = await firstValueFrom(
        this.apiService.storeBuyLoadItem(requestBody, this.isGamingPins)
      );

      if (response.status === 200) {
        if (this.isGamingPins) {
          this.toastr.success('Gaming Pin has been successfully added.', 'Success!');
        }else{
          this.toastr.success('New Buyload Item has been successfully added.', 'Success!');
        }
        this.dialogRef.close(true);
      } else {
        this.toastr.error(
          'Item ID already exists. Please check all SKU details.',
          'Something went wrong!'
        );
      }
    } catch (error: any) {
      this.toastr.error(error.error?.message || 'Something went wrong!', 'Error!');
    } finally {
      this.spinner.hide().catch(console.error);
    }
  }

  private async performUpdate(raw: any, modifiedItemPrice: number): Promise<void> {
    try {
      let response: any;

      if (this.isGamingPins) {
        const requestBody = {
          provider:         this.provider,
          providerTypes:    Array.isArray(this.providerType) ? this.providerType : [],
          itemId:           this.fetchedItem.itemId,
          itemName:         raw.itemName,
          itemCategory:     this.itemCategory,
          itemDesc:         raw.itemDesc,
          itemPrice:        modifiedItemPrice,
          redeemable:       this.isRedeemable,
          sort:             raw.sort !== 0 ? raw.sort : null,
          rebatePercentage: raw.rebatePercentage !== 0 ? raw.rebatePercentage : null,
          coinsType:        1,
        };

        response = await firstValueFrom(
          this.apiService.updateBuyloadItemPins(requestBody)
        );
      } else {
        const requestBody = {
          provider:         this.provider,
          itemName:         raw.itemName,
          itemDesc:         raw.itemDesc,
          itemPrice:        modifiedItemPrice,
          itemType:         this.itemType,
          providerType:     this.providerType,
          itemCategory:     null,
          rebatePercentage: raw.rebatePercentage !== 0 ? raw.rebatePercentage : null,
          sort:             raw.sort !== 0 ? raw.sort : null,
          redeemable:       this.isRedeemable,
          pointsAmount:     this.isRedeemable ? (raw.pointsAmount ?? 0) : 0,
        };

        response = await firstValueFrom(
          this.apiService.updateBuyLoadItem(this.fetchedItem.uid, requestBody)
        );
      }

      if (response.status === 200) {
        if (this.isGamingPins) {
          this.toastr.success('Gaming Pin Item updated successfully', 'Success!');
        }else{
          this.toastr.success('Buy Load Item updated successfully.', 'Success!');
        }
        this.dialogRef.close(true);
      } else {
        this.toastr.error(response.error?.message || 'Update failed.', 'Something went wrong!');
      }
    } catch (error: any) {
      this.toastr.error(error.error?.message || 'Something went wrong!', 'Error!');
    } finally {
      this.spinner.hide().catch(console.error);
    }
  }

  close(): void {
    this.dialogRef.close(false);
  }

  get itemTypeLockedTooltip(): string {
    return `${this.providerType_text} is for entertainment. If you want to change, kindly choose other provider type.`;
  }

}
